import { type Descendant } from 'slate';
import {
  defaultFontList,
  type FontFamily,
} from '../plugins/Formatting/FontFamilyPlugin/fontList';

// Mapping object format:
// { v1Key: v2Key }

const commonBlockMapping = {
  alignment: 'alignment',
  lineHeight: 'lineHeight',
};

const codeBlockMapping = {
  ...commonBlockMapping,
  language: 'code_language',
};

const resizerMapping = {
  width: 'resizer_width',
  height: 'resizer_height',
  originalWidth: 'resizer_originalWidth',
  originalHeight: 'resizer_originalHeight',
};

const mediaMapping = {
  src: 'media_src',
  alt: 'media_alt',
};

const resizeableMediaMapping = {
  ...resizerMapping,
  ...mediaMapping,
};

const embeddedWhiteboardMapping = {
  ...resizerMapping,
  whiteboardId: 'whiteboard_id',
};

const embeddedReportMapping = {
  ...resizerMapping,
  bookmarkId: 'bookmark_id',
};

const embedMapping = {
  ...resizerMapping,
  embed_src: 'embed_src',
};

const listItemMapping = {
  checked: 'list_checked',
};

const linkMapping = {
  href: 'link_href',
};

const pillMapping = {
  colorScheme: 'pill_colorScheme',
};

const tableMapping = {
  width: 'table_width',
  columnWidths: 'table_columnWidths',
  borderColor: 'table_borderColor',
};

const tableCellMapping = {
  rowspan: 'table_rowspan',
  colspan: 'table_colspan',
  verticalAlign: 'table_cellVerticalAlign',
  cellColor: 'table_cellBackgroundColor',
};

const userReferenceMapping = {
  class: 'reference_class',
  id: 'reference_id',
  url: 'reference_url',
  name: 'reference_name',
};

const ahaReferenceMapping = {
  class: 'reference_class',
  id: 'reference_id',
  url: 'reference_url',
  name: 'reference_name',
  reference_num: 'reference_num',
  visibleFields: 'reference_visibleFields',
};

const colorMarkMapping = {
  foreground: 'textColor',
  background: 'textHighlight',
};

type V1Data = Record<string, any>;

interface V1BaseNode {
  object: string;
  data?: V1Data;
}

export interface V1Mark {
  type: string;
  data?: V1Data;
}

// v 0.34 text nodes had leaves
interface V1OldTextNode extends V1BaseNode {
  object: 'text';
  marks?: V1Mark[];
  leaves: Array<{ object: 'leaf'; text: string }>;
}

interface V1NewTextNode extends V1BaseNode {
  object: 'text';
  marks?: V1Mark[];
  text: string;
}

export type V1TextNode = V1NewTextNode | V1OldTextNode;

export interface V1DataNode {
  type?: string;
  nodes: V1Node[];
  object: string;
  data?: V1Data;
}

export type V1Node = V1TextNode | V1DataNode;

export const isV1Node = (node: V1Node | Descendant): node is V1Node => {
  return 'object' in node && !!node.object;
};

export const isV1TextNode = (node: V1Node): node is V1TextNode =>
  node.object === 'text';

const convertProperties = (
  node: V1Node | V1Mark,
  mapping: Record<string, string>,
  // Some nodes have data nested under another object, e.g. user-reference
  nestedKey?: string
) => {
  return Object.keys(mapping).reduce<Record<string, string>>((acc, key) => {
    let value;

    if (node.data) {
      if (nestedKey && node.data[nestedKey]) {
        value = node.data[nestedKey][key];
      } else {
        value = node.data[key];
      }
    }

    if (value !== undefined && mapping[key]) {
      acc[mapping[key]] = value;
    }
    return acc;
  }, {});
};

const migrateFontFamily = (mark: any) => {
  const fontConfig = defaultFontList.filter(
    ({ styleName }: FontFamily) => styleName === mark.data.fontFamily
  );

  if (fontConfig.length > 0) return { fontFamily: fontConfig[0].fontFamily };

  return {};
};

const migrateMedia = (
  node: V1Node | V1Mark,
  mapping: Record<string, string>
) => {
  const newProps = convertProperties(node, mapping);

  // Media migrated from v1 should be inline by default
  newProps.media_position = 'inline';

  return newProps;
};

export const migrateMarkData = (mark: V1Mark) => {
  switch (mark.type) {
    case 'color':
      return convertProperties(mark, colorMarkMapping);
    case 'fontFamily':
      return migrateFontFamily(mark);
    default: {
      const value =
        mark.data && mark.data[mark.type] ? mark.data[mark.type] : true;
      return { [mark.type]: value };
    }
  }
};

function toInteger(data: Record<string, any>, key: string) {
  if (!data) return;

  const value = parseInt(data[key], 10);

  if (value && !isNaN(value)) {
    data[key] = value;
  }
}

function cleanupNullDimensions(data: Record<string, any>) {
  if (!data) return;

  for (const key of ['width', 'height', 'originalWidth', 'originalHeight']) {
    if (data[key] === null) {
      delete data[key];
    }
  }
}

export const migrateData = (node: V1DataNode) => {
  node.data ??= {};

  switch (node.type) {
    case 'paragraph':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
    case 'quote':
    case 'infoBlock':
    case 'noteBlock':
    case 'warningBlock':
    case 'tipBlock':
      return convertProperties(node, commonBlockMapping);
    case 'code':
      return convertProperties(node, codeBlockMapping);

    case 'image':
    case 'video':
      return migrateMedia(node, resizeableMediaMapping);
    case 'audio':
      return migrateMedia(node, mediaMapping);

    case 'embed':
      return convertProperties(node, embedMapping);

    case 'embedded-whiteboard':
      cleanupNullDimensions(node.data);
      return convertProperties(node, embeddedWhiteboardMapping);
    case 'embeddedReport': {
      node.data.payload ??= {};

      for (const key of ['width', 'height']) {
        // Old reports stored width and height directly on the node
        // data, not the payload.
        if (node.data.payload[key] === undefined) {
          node.data.payload[key] = node.data[key];
        }

        // Old reports stored width and height as strings, not
        // integers.
        toInteger(node.data.payload, key);
      }

      const newProps = convertProperties(
        node,
        embeddedReportMapping,
        'payload'
      );
      newProps.type = 'embedded-report';
      newProps.payload = node.data.payload;
      return newProps;
    }
    case 'list_item':
      return convertProperties(node, listItemMapping);

    case 'pill':
      return convertProperties(node, pillMapping);

    case 'link':
      return convertProperties(node, linkMapping);

    case 'table':
      return convertProperties(node, tableMapping);

    case 'table-cell':
      return convertProperties(node, tableCellMapping);

    case 'user-reference':
      return convertProperties(node, userReferenceMapping, 'reference');

    case 'aha-reference':
      return convertProperties(node, ahaReferenceMapping, 'reference');

    default:
      return Object.entries(node.data).reduce((acc, [key, value]) => {
        const propName = `${node.type}_${key}`;

        return {
          ...acc,
          [propName]: value,
        };
      }, {});
  }
};
